import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import classNames from 'classnames';
import FilterButton from '@/components/common/FilterButton';
import SearchInput from '@/components/common/SearchInput';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import { useCurrentUser } from '@/lib/hooks/userData';
import translate from '@/lib/intl/translate';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { ConstituentSortSelect } from '@/modules/ConstituentDetails';
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import { useQueryProvider } from '@/modules/QueryProvider';
import useConstituentSearchFilter from '../hooks/useConstituentSearchFilter';
import type ConstituentSearchFilterParams from '../types/ConstituentSearchFilterParams';
import ConstituentSearchDrawer from './ConstituentSearchDrawer';
import ConstituentSearchFormProvider from './ConstituentSearchFormProvider';
import QuickFilters from './QuickFilters';

export type LifecycleSearchControlsProps = {
  boardId: string;
  className?: string;
};
function BaseLifeycleSearchControls({
  boardId,
  className = '',
}: LifecycleSearchControlsProps) {
  const [isFilterModalActive, setIsFilterModalActive] = React.useState(false);
  const { updateFilters } = useConstituentSearchFilter();
  const { control } = useFormContext();
  const { openDialog, closeDialog } = useDialogManager();
  const { isOrgAdmin } = useCurrentUser();
  const { sorting, updateQuery } =
    useQueryProvider<ConstituentSearchFilterParams>();

  const onEdit = React.useCallback(() => {
    openDialog(Dialogs.CREATE_LIFECYCLE_BOARD, {
      boardId,
      onClose: closeDialog,
    });
  }, [boardId]);

  const onSortChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const [sortBy, sortOrder] = e.target.value.split(',');
      updateQuery({ sortBy, sortOrder });
    },
    [updateQuery],
  );

  return (
    <>
      <div
        className={classNames(
          className,
          'flex flex-col items-center md:flex-row',
        )}
      >
        <div className="flex w-full flex-wrap items-center gap-2 pb-4 md:gap-4 md:pb-0 lg:gap-6 ">
          <Controller
            control={control}
            name={ConstituentSearchFilterField.NAME_EMAIL_PHONE}
            render={({ field: { onChange, value } }) => (
              <SearchInput
                id="constituent-search-name-email-phone"
                backgroundColor="#fff"
                onChange={(val: string) => {
                  onChange(val);
                  updateFilters();
                }}
                className="mb-0 w-full flex-grow lg:w-auto"
                label={translate('CONSTITUENT_SEARCH')}
                placeholder={translate('CONSTITUENT_SEARCH_BY')}
                value={value}
              />
            )}
          />
          <ConstituentSortSelect
            className="mt-0 flex w-auto flex-shrink-0 flex-grow lg:w-32"
            value={[sorting.sortBy, sorting.sortOrder].join(',')}
            onChange={onSortChange}
            size="medium"
          />
          <div className="hidden md:flex">
            <QuickFilters />
          </div>
          <FilterButton
            className="md:h-12 md:px-6"
            type="button"
            variant="contained"
            color="primary"
            onClick={() => setIsFilterModalActive(true)}
            size="small"
          />
          {isOrgAdmin && (
            <Button
              className="h-12 flex-shrink-0 flex-grow-0 px-2 md:px-3"
              type="button"
              color="primary"
              variant="contained"
              onClick={onEdit}
            >
              <EditIcon />
              <span className="sr-only">{translate('BOARD_EDIT')}</span>
            </Button>
          )}
        </div>
      </div>
      <ConstituentSearchDrawer
        onClose={() => setIsFilterModalActive(false)}
        open={isFilterModalActive}
      />
    </>
  );
}

export default function LifeycleSearchControls(
  props: LifecycleSearchControlsProps,
) {
  return (
    <ConstituentSearchFormProvider>
      <BaseLifeycleSearchControls {...props} />
    </ConstituentSearchFormProvider>
  );
}
